import type { ReactNode } from 'react';
import { Alert, IconButton, Snackbar } from '@mui/material';
import type { AlertColor } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

/** The outcome of a command or toolbar action, as the user should see it. */
export interface CommandResult {
  /**
   * How the outcome reads. Most actions are success or error; info covers the "nothing to do"
   * outcome (already pinned, nothing eligible) that is neither a win nor a failure.
   */
  severity: AlertColor;
  message: string;
}

interface CommandResultSnackbarProps {
  /** The outcome to show; null keeps the snackbar closed. */
  result: CommandResult | null;
  onClose: () => void;
  /**
   * Optional control inside the alert — typically a link to what the command produced
   * ("Open run"). Keep it to one short action; the alert already has a close button.
   */
  action?: ReactNode;
}

/**
 * How long an outcome stays up. One value for the whole app, so feedback behaves the same
 * wherever an action is triggered.
 */
const AUTO_HIDE_MS = 6000;

/**
 * The one way a command or toolbar action reports its outcome: a filled alert at the bottom
 * centre, colour from the outcome, dismissible, gone after six seconds.
 *
 * Shared by the generated detail views (Actions-menu commands), the generated dashboards
 * (pin to Home) and hand-written toolbar actions, so a new action never decides a duration, a
 * position or a variant of its own — before this, five call sites had five different answers.
 * Durable state belongs elsewhere: an error the user must act on goes in the validation panel,
 * and a background job's progress goes to the notification bell.
 */
export function CommandResultSnackbar({ result, onClose, action }: CommandResultSnackbarProps) {
  const { t } = useTranslation();
  // MUI drops Alert's own close button as soon as `action` is set, so the close button is
  // carried alongside the action here — an outcome must always be dismissible.
  const alertAction = action ? (
    <>
      {action}
      <IconButton
        color="inherit"
        size="small"
        aria-label={t('common.close', { defaultValue: 'Close' })}
        onClick={onClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  ) : undefined;

  return (
    <Snackbar
      open={result !== null}
      autoHideDuration={AUTO_HIDE_MS}
      onClose={(_event, reason) => {
        // A click elsewhere must not steal an outcome the user has not read.
        if (reason !== 'clickaway') onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity={result?.severity ?? 'info'} onClose={onClose} variant="filled" action={alertAction}>
        {result?.message}
      </Alert>
    </Snackbar>
  );
}
